// PricebookPage (Pages/Setup/Price book, DEC-019: concept A's One Find, with B's pictures and C's
// queue): Setup › Price book on the ListPage pattern. The tree of systems › tasks on the left; over the
// list, Find, the health filters (No picture, No customer words, Price not set, Unused for a year, and
// Asked by techs, dashed: it waits on the office) and Table or Pictures; then the items, ticked to act
// on together. A name opens the item beside the list in a FormSheet (ItemSheet); Add an item opens a
// new one; Edit them together opens the bulk edit ("Keep as it is", Change 3 items). A filter can have
// a queue that works through it one at a time (ReviewQueue): under No customer words, Review N of Jev's
// drafts, and Approve all closes it and the counts follow (critic K10). Who looks changes it: the owner
// sees our cost; a CSR suggests, with no price fields and no ticks. Reads only --pbpage-*; every piece
// reads its own.
//   PricebookView draws a book it's given (PricebookBook): its items and categories, the filters it can
//     tell, its queue, and what saving an item or several does. The office app gives it the company's.
//   PricebookPage is the made-up book's (pricebook-data.ts), for Storybook.
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from '../../components/Button/Button.tsx';
import { Choice, ChoiceGroup } from '../../components/ChoiceGroup/ChoiceGroup.tsx';
import { SelectField, TextField } from '../../components/Field/Field.tsx';
import { Link } from '../../components/Link/Link.tsx';
import { Picture, type PictureSrc, type PictureState } from '../../components/Picture/Picture.tsx';
import { PictureGrid } from '../../components/PictureGrid/PictureGrid.tsx';
import {
  PictureTable,
  type PictureCell,
  type PictureRow,
} from '../../components/PictureTable/PictureTable.tsx';
import { ReviewQueue } from '../../components/ReviewQueue/ReviewQueue.tsx';
import { Segmented } from '../../components/Segmented/Segmented.tsx';
import { TreeList, type TreeNode } from '../../components/TreeList/TreeList.tsx';
import { money, plural } from '../../format.ts';
import { FormSheet, KEEP } from '../../patterns/FormSheet/FormSheet.tsx';
import { ListPage, type ListStatus } from '../../patterns/ListPage/ListPage.tsx';
import type { PageHeadProps } from '../../patterns/PageHead/PageHead.tsx';
import { AsksList } from '../AsksList/AsksList.tsx';
import {
  ItemSheet,
  ItemWords,
  type ItemSheetProps,
  type SheetItem,
  type SheetViewer,
  type SheetWords,
} from '../ItemSheet/ItemSheet.tsx';
import {
  ASKS,
  BY_ID,
  CAT_BY_ID,
  CATEGORIES,
  ITEMS,
  pictureOf,
  type Ask,
  type Item,
} from '../pricebook-data.ts';
import { newSheetFrom, sheetOf } from '../pricebook-story.ts';
import './PricebookPage.css';

// ───────── a book, as the page draws it ─────────

/** One item of a book: what its row, its tile and its filters read. */
export interface BookItem {
  id: string;
  categoryId: string | null;
  code: string;
  name: string;
  /** null: not set yet */
  priceCents: number | null;
  memberPriceCents: number | null;
  picture: PictureSrc | null;
  artState: PictureState;
  /** What the customer reads; undefined when the book doesn't keep them. */
  words?: SheetWords | null;
  /** Sold in the last 12 months; undefined when the book doesn't count them. */
  sold12?: number;
  /** false: no longer offered (said under its name). */
  active?: boolean;
}
export interface BookCategory {
  id: string;
  name: string;
  parentId: string | null;
}

/** The filters a book can tell: what an item lacks, or what waits on someone. */
export type BookFilter = 'nopic' | 'toapprove' | 'nowords' | 'noprice' | 'unused';
export const BOOK_FILTERS: Record<
  BookFilter,
  { label: string; test: (i: BookItem) => boolean; isWaiting?: boolean }
> = {
  nopic: { label: 'No picture', test: (i) => i.artState !== 'ready' },
  // a picture made and waiting for the owner or a manager: it waits, so it's dashed
  toapprove: { label: 'Pictures to approve', test: (i) => i.artState === 'to_approve', isWaiting: true },
  nowords: { label: 'No customer words', test: (i) => !i.words || i.words.state !== 'approved' },
  noprice: { label: 'Price not set', test: (i) => i.priceCents == null },
  unused: { label: 'Unused for a year', test: (i) => i.sold12 === 0 },
};
export type Health = 'all' | BookFilter | 'asks';

export type BookColumn = 'where' | 'price' | 'member' | 'sold' | 'words';
const COLUMNS: Record<BookColumn, { label: string; align?: 'end' }> = {
  where: { label: 'Where' },
  price: { label: 'Price', align: 'end' },
  member: { label: 'Members', align: 'end' },
  sold: { label: 'Sold, 12 mo', align: 'end' },
  words: { label: 'Words' },
};

/** What the page knows while it draws a queue: the place open and the items shown. */
export interface BookContext {
  viewer: SheetViewer;
  where: string | null;
  inWhere: (i: BookItem) => boolean;
  shown: BookItem[];
}
/** A queue through one filter's list, one record at a time (ReviewQueue). */
export interface BookQueue {
  filter: BookFilter;
  /** Over the list: "3 of these have Jev's draft waiting." */
  note: ReactNode;
  /** Its button: "Review 3 of Jev's drafts"; none, no button. */
  openLabel: ReactNode;
  /** The queue's own title block. */
  title: string;
  lede: string;
  label: string;
  total: number;
  summary?: string;
  doneText: string;
  record: (index: number) => ReactNode;
  primaryLabel: (index: number) => string;
  /** The main action; it says whether the record left the list (the next one is then at the same
   * place) or not (the queue moves on). Without it there's Next alone (who can't approve). */
  onPrimary?: (index: number) => boolean | Promise<boolean>;
  approveAll?: { label: ReactNode; run: () => void | Promise<void> };
  /** The record's item: E opens it beside the queue. */
  itemAt?: (index: number) => string | undefined;
  /** An item's sheet: how many more wait, for "See the other N waiting". */
  othersWaiting?: number;
}

/** What Edit them together did: its words, and the items that didn't change, to try again. */
export interface BulkResult {
  said: string;
  retry: string[];
}

/** A bulk field: "Where they sit", its options after Keep as it is. */
export interface BulkField {
  key: string;
  label: string;
  options: { value: string; label: string }[];
}

export interface PricebookBook {
  items: BookItem[];
  categories: BookCategory[];
  /** Loading, or failed with its words and Try again; ready otherwise (empty is worked out). */
  status?: Extract<ListStatus, 'ready' | 'loading' | 'failed'>;
  failedText?: string;
  onRetry?: () => void;
  /** The filters it can tell, in their order. */
  filters: BookFilter[];
  /** The table's columns after the item, in their order. */
  columns: BookColumn[];
  /** What techs asked the office to add: the Asked by techs filter, when the book keeps them. */
  asks?: {
    rows: Ask[];
    onAdd?: (id: string) => void;
    onJustThisJob?: (id: string) => void;
    onTurnDown?: (id: string) => void;
  };
  queue?: (ctx: BookContext) => BookQueue | null;
  /** An item's sheet, and a new one. */
  sheet: {
    /** The item's sheet as it stands (null while it can't be drawn). */
    of: (id: string) => SheetItem | null;
    /** A new item's sheet, from the similar item chosen (or none). */
    fresh: (similar: string | null) => SheetItem;
    /** A new item starts from a similar one: the choices, and the one chosen first. */
    similar?: { first: string; options: { value: string; label: string }[] };
    /** It opened: the book may read the rest of it. */
    onOpen?: (id: string) => void;
    /** Saves it; throws with the words to say when it doesn't. */
    save: (id: string | 'new', item: SheetItem) => Promise<void> | void;
    /** Who changes prices, said to a CSR. */
    pricesBy: string;
    /** An item that can't be changed here, with the line that says why (an imported one, mirrored). */
    readOnly?: (id: string) => string | null;
    /** What the sheet draws besides the item: the places to move it to, tax classes, its pictures. */
    props?: (id: string | 'new') => Partial<ItemSheetProps>;
  };
  /** Several at once: the fields, and what changing them does. */
  bulk?: {
    fields: BulkField[];
    /**
     * Runs the changes. With a result, the page says it, and the ids in `retry` stay ticked: the
     * sheet stays open on its words, so trying again sends only what didn't change.
     */
    apply: (
      ids: string[],
      changes: Record<string, string>,
    ) => Promise<BulkResult | undefined> | BulkResult | undefined;
    /** What the changes would do, said over Change before anything is sent. */
    preview?: (ids: string[], changes: Record<string, string>) => { lines: string[]; each: string[] };
  };
  /** The title block's: Spreadsheet in or out; New category under the tree. */
  onSpreadsheet?: () => void;
  onNewCategory?: () => void;
  /** Whoever edits adds items (the head's Add an item). */
  canAdd?: boolean;
  /** Under the list, the whole width: what else the page holds (the book's rules, the spreadsheet). */
  below?: ReactNode;
}

export interface PricebookStart {
  view?: 'table' | 'pictures';
  health?: Health;
  where?: string | null;
  query?: string;
  selected?: string[];
  /** An item's id, a new item, or the bulk edit, open beside the list. */
  open?: string | 'new' | 'bulk' | null;
  /** The bulk edit's choices, made: field → option. */
  bulk?: Record<string, string>;
  queue?: boolean;
  status?: 'ready' | 'loading';
}

export interface PricebookViewProps {
  viewer: SheetViewer;
  book: PricebookBook;
  start?: PricebookStart;
  /** The crumb over the title (Setup › Price book). */
  crumb?: PageHeadProps['crumb'];
}

const whole = (c: number) => `$${Math.round(c / 100).toLocaleString('en-CA')}`;

/** A row of the table, from an item and where it sits. */
function rowOf(i: BookItem, where: string, columns: BookColumn[]): PictureRow {
  const cells: Record<string, PictureCell> = {};
  for (const c of columns) {
    if (c === 'where') cells.where = where || { text: '—', tone: 'muted' };
    if (c === 'price')
      cells.price = i.priceCents == null ? { text: 'Not set', tone: 'bad' } : money(i.priceCents);
    if (c === 'member')
      cells.member =
        i.memberPriceCents == null || i.memberPriceCents === i.priceCents
          ? { text: '—', tone: 'muted' }
          : money(i.memberPriceCents);
    if (c === 'sold') cells.sold = i.sold12 ? String(i.sold12) : { text: 'none', tone: 'muted' };
    if (c === 'words')
      cells.words = !i.words
        ? { text: 'None yet', tone: 'muted' }
        : i.words.state === 'draft'
          ? { text: 'Jev’s draft', tone: 'waiting' }
          : '';
  }
  return {
    id: i.id,
    name: i.name,
    code: i.active === false ? `${i.code} · not offered` : i.code,
    picture: i.picture,
    pictureState: i.artState,
    cells,
  };
}

function match(i: BookItem, place: string, q: string) {
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hay = `${i.name} ${i.code} ${place}`.toLowerCase();
  return words.every((w) => hay.includes(w));
}

/** One record in a queue: its picture (or its light and dark), its name, and what's to judge. */
export function QueueRecord({
  pictures,
  name,
  label,
  children,
}: {
  pictures: { src: PictureSrc | null; state?: PictureState; alt?: string }[];
  name: string;
  /** Over what's to judge: "What the customer reads". */
  label?: string;
  children?: ReactNode;
}) {
  return (
    <div className="fl-pbpage__rec">
      {pictures.map((p, n) => (
        <Picture key={n} src={p.src} state={p.state} size="hero" alt={p.alt} />
      ))}
      <div className="fl-pbpage__recwords">
        <h2 className="fl-pbpage__h">{name}</h2>
        {label ? <p className="fl-pbpage__strong">{label}</p> : null}
        {children}
      </div>
    </div>
  );
}

/** Runs what follows now when the answer is in hand, or once it comes. */
function after<T>(got: T | Promise<T>, then: (v: T) => void) {
  if (got instanceof Promise) void got.then(then);
  else then(got);
}

/** The sheet with one field changed, as ItemSheet names them. */
export function changed(d: SheetItem, field: string, v: string): SheetItem {
  if (['name', 'code', 'kind', 'price', 'member', 'cost', 'reason'].includes(field))
    return { ...d, [field]: v };
  if (field === 'categoryId') return { ...d, place: { ...d.place, categoryId: v } };
  if (d.sell && (field === 'unit' || field === 'taxClass')) return { ...d, sell: { ...d.sell, [field]: v } };
  if (d.sell && (field === 'taxable' || field === 'active'))
    return { ...d, sell: { ...d.sell, [field]: v === 'true' } };
  if (!d.words) return d;
  const w = { ...d.words };
  if (field === 'wordsName') w.name = v;
  if (field === 'what') w.what = v;
  if (field === 'warranty') w.warranty = v;
  if (field === 'included') w.included = v.split('\n');
  return { ...d, words: w };
}

export function PricebookView({ viewer, book, start = {}, crumb }: PricebookViewProps) {
  const edit = viewer !== 'csr';
  const [view, setView] = useState(start.view ?? 'table');
  const [health, setHealth] = useState<Health>(start.health ?? 'all');
  const [where, setWhere] = useState<string | null>(start.where ?? null);
  const [query, setQuery] = useState(start.query ?? '');
  const [selected, setSelected] = useState<string[]>(start.selected ?? []);
  const [open, setOpen] = useState<string | null>(start.open ?? null);
  const [queueOn, setQueueOn] = useState(start.queue ?? false);
  const [qi, setQi] = useState(0);
  const [from, setFrom] = useState<string | null>(book.sheet.similar?.first ?? null);
  const [bulk, setBulk] = useState<Record<string, string>>(start.bulk ?? {});
  const [bulkSaid, setBulkSaid] = useState<string | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  // the book's places: by id, their children, the items in each (a system holds its tasks' items)
  const cats = useMemo(() => Object.fromEntries(book.categories.map((c) => [c.id, c])), [book.categories]);
  const kids = useMemo(() => {
    const m = new Map<string | null, BookCategory[]>();
    for (const c of book.categories) {
      const p = c.parentId && cats[c.parentId] ? c.parentId : null;
      m.set(p, [...(m.get(p) ?? []), c]);
    }
    return m;
  }, [book.categories, cats]);
  const pathOf = (id: string | null): BookCategory[] => {
    const out: BookCategory[] = [];
    for (
      let c = id ? cats[id] : undefined;
      c && out.length < 3;
      c = c.parentId ? cats[c.parentId] : undefined
    )
      out.unshift(c);
    return out;
  };
  const named = (id: string | null) =>
    pathOf(id)
      .map((c) => c.name)
      .join(' › ');
  const under = (catId: string | null, place: string) => pathOf(catId).some((c) => c.id === place);
  const itemsIn = (place: string) => book.items.filter((i) => under(i.categoryId, place));
  const inWhere = (i: BookItem) => (where ? under(i.categoryId, where) : true);
  const filters = book.filters;
  const counts = Object.fromEntries(
    filters.map((h) => [h, book.items.filter(BOOK_FILTERS[h].test).length]),
  ) as Record<BookFilter, number>;
  const tested = (i: BookItem) => health === 'all' || health === 'asks' || BOOK_FILTERS[health].test(i);
  const shown = book.items.filter(
    (i) =>
      inWhere(i) &&
      tested(i) &&
      (!query.trim() || match(i, (i.categoryId && cats[i.categoryId]?.name) || '', query)),
  );
  const queue = book.queue?.({ viewer, where, inWhere, shown }) ?? null;

  const nodesOf = (parent: string | null, depth: number): TreeNode[] =>
    (kids.get(parent) ?? []).map((c) => {
      const children = depth < 3 ? nodesOf(c.id, depth + 1) : [];
      return {
        id: c.id,
        label: c.name,
        count: itemsIn(c.id).length,
        ...(children.length ? { children } : {}),
      };
    });
  const nodes = nodesOf(null, 1);
  const whereLabel = where ? named(where) : 'All items';

  // the sheet: the item as the book has it (read again when its details come, or it's saved), and the
  // changes typed over it, kept until it closes
  const base =
    open && open !== 'bulk' ? (open === 'new' ? book.sheet.fresh(from) : book.sheet.of(open)) : null;
  const [edits, setEdits] = useState<SheetItem | null>(null);
  const draft = edits ?? base;
  const openItem = (id: string | 'new' | 'bulk') => {
    opener.current = document.activeElement as HTMLElement | null;
    setEdits(null);
    setOpen(id);
    if (id === 'bulk') {
      setBulk({});
      setBulkSaid(null);
    } else if (id !== 'new') book.sheet.onOpen?.(id);
  };
  const close = () => {
    setOpen(null);
    setEdits(null);
    requestAnimationFrame(() => opener.current?.focus?.());
  };
  const change = (field: string, v: string) => {
    if (draft) setEdits(changed(edits ?? draft, field, v));
  };
  const dirty = !!(edits && base && JSON.stringify(edits) !== JSON.stringify(base));

  const startQueue = () => {
    opener.current = document.activeElement as HTMLElement | null;
    setQi(0);
    setQueueOn(true);
  };

  const tools = (
    <div className="fl-pbpage__tools">
      <div className="fl-pbpage__find">
        <TextField
          type="search"
          label="Find in the price book"
          hideLabel
          placeholder="Find: name, code or “wh 50 gas”"
          value={query}
          onChange={setQuery}
        />
      </div>
      <ChoiceGroup
        label="Show"
        selectionMode="single"
        value={health}
        onChange={(h) => {
          setHealth(h as Health);
          setSelected([]);
        }}
      >
        <Choice value="all" count={book.items.filter(inWhere).length}>
          Everything
        </Choice>
        {/* a waiting filter with nothing waiting says nothing */}
        {filters
          .filter((h) => !BOOK_FILTERS[h].isWaiting || counts[h] > 0 || health === h)
          .map((h) => (
            <Choice key={h} value={h} count={counts[h]} isWaiting={BOOK_FILTERS[h].isWaiting}>
              {BOOK_FILTERS[h].label}
            </Choice>
          ))}
        {book.asks ? (
          <Choice value="asks" count={book.asks.rows.length} isWaiting>
            Asked by techs
          </Choice>
        ) : null}
      </ChoiceGroup>
      {health === 'asks' ? null : (
        <Segmented
          label="Show them as"
          items={[
            { id: 'table', label: 'Table' },
            { id: 'pictures', label: 'Pictures' },
          ]}
          selectedKey={view}
          onSelectionChange={(v) => setView(v as 'table' | 'pictures')}
        />
      )}
    </div>
  );

  const bulkOpen = (preset: Record<string, string> = {}) => {
    openItem('bulk');
    setBulk(preset);
  };
  const bulkBar =
    edit && book.bulk && selected.length && health !== 'asks' ? (
      <div className="fl-pbpage__bulk" role="region" aria-label="Ticked items">
        <b className="fl-pbpage__strong">{plural(selected.length, 'item')} ticked</b>
        <Button onPress={() => bulkOpen()}>Edit them together</Button>
        {book.bulk.fields.some((f) => f.key === 'where') ? (
          <Link onPress={() => bulkOpen()}>Move to…</Link>
        ) : null}
        {book.bulk.fields.some((f) => f.key === 'sale') ? (
          <Link onPress={() => bulkOpen({ sale: 'no' })}>Retire</Link>
        ) : null}
        <Link onPress={() => setSelected([])}>Clear</Link>
      </div>
    ) : null;
  const queueNote =
    queue && health === queue.filter && queue.openLabel ? (
      <div className="fl-pbpage__review">
        <p className="fl-pbpage__note">{queue.note}</p>
        <Button onPress={startQueue}>{queue.openLabel}</Button>
      </div>
    ) : null;

  const tileNote = (i: BookItem) =>
    i.priceCents == null
      ? { text: 'Price not set', tone: 'bad' as const }
      : i.artState !== 'ready'
        ? { text: 'No picture yet', tone: 'muted' as const }
        : i.words?.state === 'draft'
          ? { text: 'Jev’s draft waiting', tone: 'waiting' as const }
          : i.words === null
            ? { text: 'No customer words', tone: 'muted' as const }
            : i.active === false
              ? { text: 'Not offered', tone: 'muted' as const }
              : undefined;

  const list =
    health === 'asks' && book.asks ? (
      <AsksList
        asks={book.asks.rows}
        {...(edit
          ? {
              onAdd: book.asks.onAdd ?? (() => openItem('new')),
              onJustThisJob: book.asks.onJustThisJob ?? (() => undefined),
              onTurnDown: book.asks.onTurnDown ?? (() => undefined),
            }
          : {})}
      />
    ) : (
      <div className="fl-pbpage__list">
        {queueNote}
        {bulkSaid ? (
          <p className="fl-pbpage__note" role="status">
            {bulkSaid}
          </p>
        ) : null}
        {bulkBar}
        {view === 'table' ? (
          <PictureTable
            label="Items"
            nameLabel="Item"
            columns={book.columns.map((c) => ({ id: c, ...COLUMNS[c] }))}
            rows={shown.map((i) => rowOf(i, named(i.categoryId), book.columns))}
            selected={selected}
            onSelect={edit && book.bulk ? setSelected : undefined}
            current={open && open !== 'new' && open !== 'bulk' ? open : null}
            onOpen={openItem}
          />
        ) : (
          <PictureGrid
            label={whereLabel}
            tiles={shown.map((i) => ({
              id: i.id,
              name: i.name,
              picture: i.picture,
              pictureState: i.artState,
              price: i.priceCents == null ? undefined : whole(i.priceCents),
              note: tileNote(i),
            }))}
            onOpen={openItem}
          />
        )}
      </div>
    );

  const filterLabel =
    health === 'asks' ? 'Asked by techs' : health === 'all' ? '' : BOOK_FILTERS[health].label;
  const title = health === 'all' ? whereLabel : `${filterLabel}${where ? ` · ${whereLabel}` : ''}`;
  const status: ListStatus =
    book.status === 'loading' || start.status === 'loading'
      ? 'loading'
      : book.status === 'failed'
        ? 'failed'
        : shown.length || health === 'asks'
          ? 'ready'
          : 'empty';
  const emptyText = query.trim()
    ? `Nothing in the book matches “${query.trim()}”. Check the spelling, or look in All items.`
    : where && !itemsIn(where).length
      ? `Nothing in ${whereLabel} yet.${edit ? ' Add an item, or move some here.' : ''}`
      : 'Nothing here: every item has what this asks for.';

  const isNew = open === 'new';
  const lockedLine = open && !isNew && open !== 'bulk' ? (book.sheet.readOnly?.(open) ?? null) : null;
  const sheetEdits = edit && !lockedLine;
  const extra = open && open !== 'bulk' ? (book.sheet.props?.(isNew ? 'new' : open) ?? {}) : {};
  const queuedId = (n: number) => queue?.itemAt?.(n);
  const sheetLayer =
    draft && open && open !== 'bulk' ? (
      <FormSheet
        isOpen
        onClose={close}
        title={isNew ? 'A new item' : draft.name}
        line={isNew ? 'It joins the book once it has a name and a price.' : draft.code}
        submitLabel={
          sheetEdits
            ? isNew
              ? 'Add to the book'
              : 'Save changes'
            : isNew
              ? 'Send for an OK'
              : 'Suggest these changes'
        }
        note={
          lockedLine ??
          (sheetEdits ? undefined : 'Your changes go to the office as a suggestion: they say yes or no.')
        }
        isReadOnly={!!lockedLine}
        isDirty={dirty}
        onSubmit={() => book.sheet.save(isNew ? 'new' : open, draft)}
      >
        <ItemSheet
          item={draft}
          viewer={viewer}
          readOnly={!!lockedLine}
          mode={isNew ? 'new' : 'edit'}
          pricesBy={book.sheet.pricesBy}
          othersWaiting={queue?.othersWaiting || undefined}
          onReview={
            queue
              ? () => {
                  setOpen(null);
                  setHealth(queue.filter);
                  setQi(0);
                  setQueueOn(true);
                }
              : undefined
          }
          onChange={change}
          similar={
            book.sheet.similar && from
              ? {
                  value: from,
                  onChange: (v) => {
                    setEdits(null);
                    setFrom(v);
                  },
                  options: book.sheet.similar.options,
                }
              : undefined
          }
          {...extra}
        />
      </FormSheet>
    ) : null;

  const bulkChanges = Object.values(bulk).filter((v) => v !== KEEP.value).length;
  const bulkPicked = Object.fromEntries(Object.entries(bulk).filter(([, v]) => v !== KEEP.value));
  const bulkWill = open === 'bulk' && bulkChanges ? book.bulk?.preview?.(selected, bulkPicked) : undefined;
  const bulkLayer =
    open === 'bulk' && book.bulk ? (
      <FormSheet
        isOpen
        onClose={close}
        title={`Change ${plural(selected.length, 'item')}`}
        line={selected.map((id) => book.items.find((i) => i.id === id)?.name).join(' · ')}
        bulk={{ count: selected.length, noun: 'item', changes: bulkChanges }}
        isDirty={bulkChanges > 0}
        onSubmit={async () => {
          const r = await book.bulk!.apply(selected, bulkPicked);
          if (r) setBulkSaid(r.said);
          // what didn't change stays ticked, the sheet open on why; what did is never sent again
          if (r?.retry.length) {
            setSelected(r.retry);
            throw new Error(r.said);
          }
          setSelected([]);
        }}
      >
        <div className="fl-pbpage__fields">
          {book.bulk.fields.map((f) => (
            <SelectField
              key={f.key}
              label={f.label}
              value={bulk[f.key] ?? KEEP.value}
              onChange={(v) => setBulk((b) => ({ ...b, [f.key]: v }))}
              options={[KEEP, ...f.options]}
            />
          ))}
        </div>
        {bulkWill ? (
          <div className="fl-pbpage__will" aria-label="What changes">
            {bulkWill.lines.map((l) => (
              <p key={l} className="fl-pbpage__strong">
                {l}
              </p>
            ))}
            {bulkWill.each.length ? (
              <ul className="fl-pbpage__each">
                {bulkWill.each.map((l, n) => (
                  <li key={n}>{l}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        <p className="fl-pbpage__note">Sent estimates keep the prices they were sent at.</p>
      </FormSheet>
    ) : null;

  const queueView =
    queueOn && queue && health === queue.filter ? (
      <ReviewQueue
        label={queue.label}
        index={qi}
        total={queue.total}
        summary={queue.summary}
        extra={
          edit && queue.approveAll ? (
            <Link onPress={() => after(queue.approveAll!.run(), () => setQueueOn(false))}>
              {queue.approveAll.label}
            </Link>
          ) : undefined
        }
        primaryLabel={queue.primaryLabel(qi)}
        onPrimary={
          edit && queue.onPrimary
            ? () =>
                after(queue.onPrimary!(qi), (left) => {
                  if (!left) setQi((n) => n + 1);
                })
            : undefined
        }
        onEdit={queuedId(qi) ? () => openItem(queuedId(qi)!) : undefined}
        onSkip={() => setQi((n) => n + 1)}
        onNext={() => setQi((n) => n + 1)}
        onBack={() => setQi((n) => Math.max(n - 1, 0))}
        onClose={() => setQueueOn(false)}
        closeLabel="Back to the list"
        doneText={queue.doneText}
      >
        {qi < queue.total ? queue.record(qi) : null}
      </ReviewQueue>
    ) : null;

  const setupCrumb = crumb ?? [{ label: 'Setup', href: '#/setup' }];
  return (
    <ListPage
      head={
        queueView && queue
          ? {
              crumb: [...setupCrumb, { label: 'Price book', onPress: () => setQueueOn(false) }],
              title: queue.title,
              lede: queue.lede,
            }
          : {
              crumb: [...setupCrumb, { label: 'Price book' }],
              title: 'Price book',
              lede:
                viewer === 'owner'
                  ? 'What the techs and the office sell from. Our cost shows to you alone.'
                  : viewer === 'csr'
                    ? 'What the techs and the office sell from. Suggest a change and the office says yes or no.'
                    : 'What the techs and the office sell from.',
              actions:
                edit && (book.canAdd ?? true) ? (
                  <>
                    {book.onSpreadsheet ? (
                      <Link onPress={book.onSpreadsheet}>Spreadsheet in or out</Link>
                    ) : null}
                    <Button variant="primary" onPress={() => openItem('new')}>
                      Add an item
                    </Button>
                  </>
                ) : undefined,
            }
      }
      rail={
        <TreeList
          label="Where items sit"
          root={{ label: 'All items', count: book.items.length }}
          nodes={nodes}
          value={where}
          onChange={(w) => {
            setWhere(w);
            setSelected([]);
          }}
          foot={
            edit && book.onNewCategory ? <Link onPress={book.onNewCategory}>New category</Link> : undefined
          }
        />
      }
      filters={tools}
      list={{
        title,
        count: health === 'asks' ? (book.asks?.rows.length ?? 0) : shown.length,
        status,
        children: list,
        emptyText,
        failedText: book.failedText ?? 'The price book didn’t load.',
        what: 'the price book',
        onRetry: book.onRetry,
        loadingText: 'Loading the price book',
        loadingRows: 6,
      }}
      replace={queueView}
      below={queueView ? undefined : book.below}
      layers={
        <>
          {sheetLayer}
          {bulkLayer}
        </>
      }
    />
  );
}

// ───────── the made-up book (Storybook) ─────────

const PRICES_BY = 'Nora, or someone she names,';
const asBook = (i: Item): BookItem => ({
  id: i.id,
  categoryId: i.categoryId,
  code: i.code,
  name: i.name,
  priceCents: i.priceCents,
  memberPriceCents: i.memberPriceCents,
  picture: pictureOf(i),
  artState: i.artState,
  words: i.words,
  sold12: i.sold12,
});

/** The made-up book, with what's approved here kept (the counts follow). */
export function useMadeUpBook(): PricebookBook {
  const [approved, setApproved] = useState<Set<string>>(new Set());
  const items = useMemo(
    () =>
      ITEMS.map((i) =>
        approved.has(i.id) && i.words ? { ...i, words: { ...i.words, state: 'approved' as const } } : i,
      ),
    [approved],
  );
  const book = useMemo(() => items.map(asBook), [items]);
  const nowords = BOOK_FILTERS.nowords.test;
  return {
    items: book,
    categories: CATEGORIES,
    filters: ['nopic', 'nowords', 'noprice', 'unused'],
    columns: ['where', 'price', 'member', 'sold', 'words'],
    asks: { rows: ASKS },
    canAdd: true,
    onSpreadsheet: () => undefined,
    onNewCategory: () => undefined,
    bulk: {
      fields: [
        {
          key: 'where',
          label: 'Where they sit',
          options: CATEGORIES.filter((c) => c.parentId).map((c) => ({
            value: c.id,
            label: `${CAT_BY_ID[c.parentId!]!.name} › ${c.name}`,
          })),
        },
        {
          key: 'price',
          label: 'Price',
          options: [
            { value: 'up5', label: 'Up 5 %' },
            { value: 'up10', label: 'Up 10 %' },
            { value: 'down5', label: 'Down 5 %' },
          ],
        },
        {
          key: 'member',
          label: 'Members pay',
          options: [
            { value: 'm10', label: '10 % off the price' },
            { value: 'same', label: 'The same as everyone' },
          ],
        },
        {
          key: 'warranty',
          label: 'Warranty line',
          options: [
            { value: '1y', label: '1 year on the labour' },
            { value: '2y', label: '2 years on the labour' },
          ],
        },
        {
          key: 'words',
          label: 'Customer words',
          options: [
            { value: 'jev', label: 'Ask Jev to draft them' },
            { value: 'approve', label: 'Approve Jev’s drafts' },
          ],
        },
        {
          key: 'sale',
          label: 'For sale',
          options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No: retired' },
          ],
        },
      ],
      apply: () => undefined,
    },
    sheet: {
      of: (id) => (BY_ID[id] ? sheetOf(items.find((i) => i.id === id)!) : null),
      fresh: (similar) => newSheetFrom(BY_ID[similar ?? 'kfb']!),
      similar: {
        first: 'kfb',
        options: ITEMS.filter((i) => i.words).map((i) => ({ value: i.id, label: i.name })),
      },
      save: () => undefined,
      pricesBy: PRICES_BY,
    },
    queue: ({ viewer, inWhere, shown }) => {
      const drafts = shown.filter((i) => i.words?.state === 'draft');
      const q = book.filter((i) => inWhere(i) && nowords(i));
      const waiting = q.filter((i) => i.words?.state === 'draft');
      return {
        filter: 'nowords',
        note: drafts.length ? (
          <>{plural(drafts.length, 'of these has', 'of these have')} Jev’s draft waiting.</>
        ) : null,
        openLabel: drafts.length ? <>Review {drafts.length} of Jev’s drafts</> : null,
        title: 'What needs doing',
        lede: 'Jev’s customer words, one item at a time: approve, change or skip.',
        label: 'No customer words',
        total: q.length,
        summary: `${waiting.length} with Jev’s draft, ${q.filter((i) => !i.words).length} with none yet`,
        doneText: 'That’s all of them. The list shows what’s left.',
        othersWaiting: Math.max(drafts.length - 1, 0),
        record: (n) => {
          const it = q[n];
          return it ? (
            <QueueRecord
              pictures={[{ src: it.picture, state: it.artState, alt: it.name }]}
              name={it.name}
              label="What the customer reads"
            >
              <ItemWords words={it.words ?? null} viewer={viewer} inQueue />
            </QueueRecord>
          ) : null;
        },
        itemAt: (n) => q[n]?.id,
        primaryLabel: (n) => (q[n]?.words ? 'Approve and next' : 'Ask Jev, and next'),
        onPrimary: (n) => {
          const it = q[n];
          if (!it?.words) return false;
          setApproved((s) => new Set([...s, it.id]));
          return true;
        },
        approveAll: waiting.length
          ? {
              label: <>Approve all {waiting.length} of Jev’s drafts</>,
              run: () => setApproved((s) => new Set([...s, ...waiting.map((i) => i.id)])),
            }
          : undefined,
      };
    },
  };
}

export interface PricebookPageProps {
  viewer: SheetViewer;
  start?: PricebookStart;
}

/** The page on the made-up book (Storybook). */
export function PricebookPage({ viewer, start }: PricebookPageProps) {
  const book = useMadeUpBook();
  return <PricebookView viewer={viewer} book={book} start={start} />;
}
